import cbor from "cbor-js"
import dayjs from "dayjs"

export function formatCurrency(
    _value: number | string,
    opts?: {
        currency?: string
        minimumFractionDigits?: number
        maximumFractionDigits?: number
    },
) {
    const maximumFractionDigits = opts?.maximumFractionDigits ?? 2
    const value = Number(
        (typeof _value === "string" ? parseFloat(_value) : _value).toFixed(maximumFractionDigits),
    )

    return Intl.NumberFormat("en-US", {
        style: "currency",
        currency: opts?.currency || "PHP",
        minimumFractionDigits: opts?.minimumFractionDigits ?? 2,
        maximumFractionDigits,
    }).format(value)
}

export const PayloadFormatter = {
    encode: (payload: Record<string, unknown>): string => {
        // 使用 Uint8Array 而不是 Buffer
        const encoded = cbor.encode(payload)
        // 將二進制數據轉換為正確的 base64 字符串
        return btoa(
            Array.from(new Uint8Array(encoded))
                .map((b) => String.fromCharCode(b))
                .join(""),
        )
    },
    decode: (base64: string): Record<string, unknown> => {
        try {
            // 從 base64 轉換為二進制數據
            const binary = atob(base64)
            const bytes = new Uint8Array(binary.length)
            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i)
            }
            // 使用 cbor 解碼二進制數據
            return cbor.decode(bytes.buffer)
        } catch (error) {
            console.error("PayloadFormatter.decode failed:", error)
            return {}
        }
    },
    jsonParse: (json?: string): Record<string, unknown> => {
        try {
            return JSON.parse(json || "{}")
        } catch (error) {
            console.error("PayloadFormatter.jsonParse failed:", error)
            return {}
        }
    },
    jsonStringify: (payload: Record<string, unknown>): string => {
        try {
            return JSON.stringify(payload)
        } catch (error) {
            console.error("PayloadFormatter.jsonStringify failed:", error)
            return "{}"
        }
    },
}

/**
 * 格式化日期
 * @param timestamp 时间戳
 * @returns 格式化后的日期
 */
export const formatDate = (timestamp: number, format: string = "MMM D, YYYY h:mm A") => {
    if (!timestamp) return ""
    return dayjs(timestamp).format(format)
}

/**
 * 通用脱敏函数
 * @param str 原始字符串
 * @param start 保留前几位
 * @param end 保留后几位
 * @param maskLength 脱敏长度（默认为4）
 * @param maskChar 脱敏字符（默认为*）
 * @returns 脱敏后的字符串
 */
export function maskString(
    str: string,
    start: number = 4,
    end: number = 3,
    maskLength: number = 4,
    maskChar: string = "*",
): string {
    if (!str || str.length <= start + end) return str
    const visibleStart = str.slice(0, start)
    const visibleEnd = str.slice(-end)
    const maskedPart = maskChar.repeat(maskLength)
    return `${visibleStart}${maskedPart}${visibleEnd}`
}

// 因为formatCurrency会自动带上比索的符号，格式化金额后去掉符号
export const formatCurrencyWithoutSymbol = (value: number) => {
    const formatted = formatCurrency(value, {
        currency: "PHP",
        minimumFractionDigits: 0,
    })
    return formatted.replace("₱", "")
}
